import type { Lang } from "./api";

const LOCALES: Record<Lang, string> = { pt: "pt-PT", en: "en-GB", es: "es-ES", fr: "fr-FR", de: "de-DE" };

export const localeOf = (lang: Lang) => LOCALES[lang];

const safeTz = (tz: string | undefined) => {
  if (!tz) return undefined;
  try {
    new Intl.DateTimeFormat("en", { timeZone: tz });
    return tz;
  } catch {
    return undefined;
  }
};

/** A calendar date (YYYY-MM-DD) as "12 Jun 2026". */
export function formatDate(iso: string | null | undefined, lang: Lang, options: Intl.DateTimeFormatOptions = { day: "numeric", month: "short", year: "numeric" }) {
  if (!iso) return "";
  const [y, m, d] = iso.slice(0, 10).split("-").map(Number);
  if (!y || !m || !d) return iso;
  return new Intl.DateTimeFormat(localeOf(lang), { ...options, timeZone: "UTC" }).format(new Date(Date.UTC(y, m - 1, d)));
}

/** "12 – 19 Jun 2026" for a trip's dates. */
export function formatDateRange(start: string | null, end: string | null, lang: Lang) {
  if (!start) return "";
  if (!end || end === start) return formatDate(start, lang);
  const [sy, sm] = start.split("-");
  const [ey, em] = end.split("-");
  if (sy === ey && sm === em) return `${formatDate(start, lang, { day: "numeric" })} – ${formatDate(end, lang)}`;
  if (sy === ey) return `${formatDate(start, lang, { day: "numeric", month: "short" })} – ${formatDate(end, lang)}`;
  return `${formatDate(start, lang)} – ${formatDate(end, lang)}`;
}

/** An instant in the trip's time zone: "14:35". */
export function formatTime(iso: string | null | undefined, lang: Lang, tz?: string) {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  const timeZone = safeTz(tz);
  return new Intl.DateTimeFormat(localeOf(lang), { hour: "2-digit", minute: "2-digit", ...(timeZone ? { timeZone } : {}) }).format(date);
}

/** An instant as "Thu 12 Jun, 14:35" in the trip's time zone. */
export function formatDateTime(iso: string | null | undefined, lang: Lang, tz?: string) {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  const timeZone = safeTz(tz);
  return new Intl.DateTimeFormat(localeOf(lang), { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", ...(timeZone ? { timeZone } : {}) }).format(date);
}

/** The calendar day of an instant in a time zone, as YYYY-MM-DD; used to group a timeline by day. */
export function dayKey(iso: string | null, tz: string): string {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  const timeZone = safeTz(tz) ?? "UTC";
  const parts = new Intl.DateTimeFormat("en-CA", { timeZone, year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(date);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? "";
  return `${get("year")}-${get("month")}-${get("day")}`;
}

/** "Thursday, 12 June" heading for a day. */
export function formatDayHeading(key: string, lang: Lang) {
  return formatDate(key, lang, { weekday: "long", day: "numeric", month: "long" });
}

export function formatMoney(minor: number, currency: string, lang: Lang) {
  try {
    return new Intl.NumberFormat(localeOf(lang), { style: "currency", currency, maximumFractionDigits: 2 }).format(minor / 100);
  } catch {
    return `${(minor / 100).toFixed(2)} ${currency}`;
  }
}

export function formatNumber(n: number, lang: Lang) {
  return new Intl.NumberFormat(localeOf(lang)).format(n);
}

/** Whole days from today (local) until a calendar date; negative when past. */
export function daysUntil(iso: string | null): number | null {
  if (!iso) return null;
  const [y, m, d] = iso.slice(0, 10).split("-").map(Number);
  if (!y || !m || !d) return null;
  const now = new Date();
  const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((Date.UTC(y, m - 1, d) - today) / 86_400_000);
}

export function relativeTime(iso: string, lang: Lang) {
  const diff = (new Date(iso).getTime() - Date.now()) / 1000;
  const rtf = new Intl.RelativeTimeFormat(localeOf(lang), { numeric: "auto" });
  const abs = Math.abs(diff);
  if (abs < 60) return rtf.format(Math.round(diff), "second");
  if (abs < 3600) return rtf.format(Math.round(diff / 60), "minute");
  if (abs < 86_400) return rtf.format(Math.round(diff / 3600), "hour");
  return rtf.format(Math.round(diff / 86_400), "day");
}
